import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, Wallet, CalendarDays, Sparkles } from "lucide-react";
import { COURSE_LIST } from "@/data/courses";
import { FEATURED_COURSE_IDS } from "@/data/featuredCourses";
import Reveal from "@/components/site/Reveal";
import BookingDialog from "@/components/site/BookingDialog";

/**
 * FeaturedCourses · „Beliebteste Weiterbildungen" — Top-5-Kurse direkt unter dem
 * Hero. Klare große Karten mit prominentem „Jetzt buchen"-CTA. Adressiert das
 * Conversion-Problem, dass die alten Kategorie-Tabs erst aktiv geklickt werden
 * mussten, bevor Nutzer Kurse sahen.
 */
export default function FeaturedCourses() {
  const featured = FEATURED_COURSE_IDS
    .map((id) => COURSE_LIST.find((c) => c.id === id))
    .filter(Boolean);

  const [bookingCourse, setBookingCourse] = useState(null);
  const [bookingOpen, setBookingOpen] = useState(false);

  const openBooking = (course) => {
    setBookingCourse(course);
    setBookingOpen(true);
  };

  return (
    <section
      id="beliebte-kurse"
      data-testid="featured-courses"
      className="relative bg-cream-100 py-24 md:py-36 border-t border-cream-300"
    >
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="max-w-3xl mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-[3px] w-12 bg-terra" />
              <span className="text-[11px] uppercase tracking-[0.28em] font-bold text-terra inline-flex items-center gap-2">
                <Sparkles className="h-3.5 w-3.5" strokeWidth={2} />
                Beliebteste Weiterbildungen
              </span>
            </div>
            <h2 className="font-magazine font-medium text-sand-800 leading-[1.08] text-[2.8rem] sm:text-[3.8rem] lg:text-[4.6rem]">
              Die Kurse,{" "}
              <span className="italic text-terra underline decoration-saffron-dark decoration-[3px] underline-offset-[10px]">
                die alle buchen.
              </span>
            </h2>
            <p className="mt-5 text-sand-700 text-base sm:text-lg leading-[1.7] max-w-2xl">
              Direkt buchen oder Details ansehen — jeden Montag startklar, AZAV-zertifiziert, Bildungsgutschein für ausgewählte Kurse.
            </p>
          </div>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((course, idx) => (
            <Reveal key={course.id} delay={idx * 60}>
              <FeaturedCard course={course} onBook={openBooking} />
            </Reveal>
          ))}
        </div>

        <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-cream-300 pt-8">
          <p className="text-sm text-sand-600">
            Du suchst etwas Anderes? Wir haben über 15 weitere Kurse — vom Einstieg bis zur Führung.
          </p>
          <a
            href="#kurse"
            data-testid="featured-courses-show-all"
            className="inline-flex items-center gap-2 rounded-full border-2 border-sand-800 text-sand-800 hover:bg-sand-800 hover:text-cream-50 px-6 py-3 text-sm font-semibold transition-all"
          >
            Alle Kurse ansehen
            <ArrowUpRight className="h-4 w-4" strokeWidth={2} />
          </a>
        </div>
      </div>

      <BookingDialog course={bookingCourse} open={bookingOpen} onOpenChange={setBookingOpen} />
    </section>
  );
}

function FeaturedCard({ course, onBook }) {
  const isFunded = (course.funding || "").toLowerCase().includes("bildungsgutschein");
  const priceNum = (() => {
    const s = String(course.price).replace(/[^\d.,]/g, "").replace(/\./g, "").replace(",", ".");
    return Number(s) || 0;
  })();
  const monthly = priceNum >= 300
    ? (Math.floor((priceNum * 100) / 12) / 100).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : null;

  return (
    <div
      data-testid={`featured-card-${course.id}`}
      className="group relative h-full flex flex-col rounded-3xl bg-white border-2 border-cream-300 p-7 pt-8 shadow-soft hover:shadow-hover transition-all hover:-translate-y-1.5 hover:border-terra/60 overflow-hidden"
    >
      <span aria-hidden="true" className="absolute inset-x-0 top-0 h-1.5 bg-terra" />

      <div className="flex items-start justify-between gap-3">
        {isFunded ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-saffron text-espresso-deep px-3 py-1 text-[10px] font-black uppercase tracking-wider">
            <Wallet className="h-3 w-3" strokeWidth={2} />
            Bildungsgutschein möglich
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-cream-200 text-sand-700 px-3 py-1 text-[10px] font-bold uppercase tracking-wider">
            Direktbuchung
          </span>
        )}
        <span className="inline-flex items-center gap-1 rounded-full bg-espresso-deep text-cream-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider">
          <CalendarDays className="h-3 w-3" strokeWidth={2} />
          {course.nextStart}
        </span>
      </div>

      <h3 className="mt-5 font-magazine font-medium text-[1.9rem] text-sand-800 leading-[1.08] group-hover:text-terra transition-colors">
        {course.title}
      </h3>
      <p className="mt-3 text-[15px] text-sand-700 leading-snug">{course.subtitle}</p>

      <div className="mt-5 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-sand-600">
        <span className="inline-flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5" strokeWidth={1.8} />
          {course.weeks} · {course.duration}
        </span>
      </div>

      <div className="flex-1" />

      <div className="mt-6 pt-5 border-t border-cream-300 flex items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-sand-500 font-semibold">Preis</div>
          <div className="font-magazine text-[1.9rem] text-terra mt-0.5 font-semibold leading-none">{course.price}</div>
          {monthly && (
            <div className="mt-1 text-[11px] font-bold text-sage-dark">
              oder ab {monthly} €/Monat
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1.5 items-end">
          <button
            type="button"
            onClick={() => onBook(course)}
            data-testid={`featured-card-book-${course.id}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-espresso-deep text-cream-50 hover:bg-terra px-6 py-3 text-[14px] font-bold transition-all hover:gap-2.5"
          >
            Jetzt buchen
            <ArrowUpRight className="h-4 w-4" strokeWidth={2.2} />
          </button>
          <Link
            to={`/kurse/${course.id}`}
            data-testid={`featured-card-details-${course.id}`}
            className="text-xs text-sand-600 underline underline-offset-2 hover:text-sand-800"
          >
            Details ansehen
          </Link>
        </div>
      </div>
    </div>
  );
}
